import {
  ReleaseFieldType,
  ReleaseSongFieldType,
} from 'services/store/distribution/types';

export default {
  release: 'Release',
  releaseTitle: 'Create new release',
  emptyState: 'You’ve not released any tracks yet.',
  draftEmptyState: 'You have no draft releases.',
  pendingEmptyState: 'You have no pending releases.',
  releases: {
    heading: 'Releases',
    list: {
      all: 'All',
      draft: 'Draft',
      pending: 'Pending',
      released: 'Released',
    },
  },
  releaseStatus: {
    released: 'RELEASED',
    pending: 'PENDING APPROVAL',
    draft: 'DRAFT',
    declined: 'DECLINED',
  },
  validation: {
    coverArt: {
      title: 'Unable to upload image',
      message: {
        invalidFileType:
          'The file type is not supported. Please try another file.',
        fileTooLarge:
          'The maximum file size is 10 MB. Please try another file.',
        imageToSmall:
          'The minimum image resolution is 500 x 500px. Please try another file.',
        imageNotSquare:
          'The image should be a square. Please try another file.',
        tooManyFiles: 'Only one image at a time is allowed.',
        generic: 'This cover art cannot be used, please try another.',
      },
    },
  },
  artistName: {
    title: 'Artist Name',
    description:
      'Your chosen artist name will be used for all releases made via Loudly.',
    placeholder: 'Artist name',
    confirm: 'Confirm Artist Name',
    multipleArtistName: 'Want multiple artist names?',
    upgradeToAnnual: 'Upgrade to Annual',
    artistAmount:
      '<0>{{artistAmount}}</0> of <1>{{totalAmount}}</1> artist profiles created.',
    createNewArtist: 'Create New Artist',
    inputLabel: 'Artist name *',
  },
  tracklist: {
    title: 'Tracklist',
    description:
      'Enter the metadata for you track, including the title, artist credits, and additional details.',
    songs: {
      sections: {
        track: 'Track',
        duration: 'Duration',
        status: 'Status',
      },
      source: {
        userUploaded: 'User uploaded audio',
        createdUsingLoudly: 'Created using Loudly',
      },
      metadata: {
        edit: 'Edit meta data',
        advancedOptions: 'Advanced options',
        valid: 'Meta data complete',
        warning: 'Incomplete meta data',
        error: 'Invalid meta data',
      },
    },
    actions: {
      confirm: 'Confirm Track',
    },
    fields: {
      hasVocals: {
        title: 'Has vocals?',
      },
      [ReleaseFieldType.SongTitle]: {
        name: 'track name',
        placeholder: 'Track name *',
      },
      [ReleaseSongFieldType.ComposerName]: {
        name: 'track composer',
        placeholder: 'Track composer *',
      },
      [ReleaseSongFieldType.Language]: {
        name: 'language',
        placeholder: 'Language *',
      },
      [ReleaseSongFieldType.VocalsContent]: {
        name: 'content',
        placeholder: 'Content *',
      },
      [ReleaseSongFieldType.VocalistName]: {
        name: 'track vocalist',
        placeholder: 'Track vocalist (optional)',
      },
      [ReleaseSongFieldType.AdditionalArtists]: {
        name: 'additional artists',
        placeholder: 'Additional artists (optional)',
      },
      [ReleaseSongFieldType.SongRemixers]: {
        name: 'track remixers',
        placeholder: 'Track remixers (optional)',
      },
      [ReleaseSongFieldType.SongProducers]: {
        name: 'track producers',
        placeholder: 'Track producers (optional)',
      },
    },
  },
  details: {
    title: 'Release details',
    description:
      'Input essential track details to enhance your music’s visibility and impact.',
    actions: {
      goBack: 'Edit Tracklist',
      continue: 'Continue',
    },
    fields: {
      releaseTitle: {
        name: 'release title',
        placeholder: 'Release title *',
      },
      [ReleaseFieldType.Genre]: {
        name: 'genre',
        placeholder: 'Genre *',
      },
      [ReleaseFieldType.CoverArt]: {
        name: 'cover art',
      },
    },
    coverArt: {
      title: 'Cover art',
      limits: '500 x 500px minimum (JPG file, up to 10 MB)',
      hint: 'Drop image here to upload<br/> or <0>browse</0> to select file',
      hover: 'Drop image to upload',
    },
  },
  review: {
    title: 'Review Your Release',
    description:
      'Once submitted, we’ll start reviewing your release. You will be notified by email when you released is live.',
    track: 'Track',
    tracks: 'Tracks',
    actions: {
      goBack: 'Edit Details',
      submitForApproval: 'Submit For Approval',
    },
  },
  guidelines:
    'Please note that streaming services and social networks adhere to strict content guidelines. Your release approval is subject to internal approval and approval by third party services and stores. Review the <0>Apple Music style guide</0> for additional details about these guidelines. We may make changes to your release to comply with these guidelines.',
  tooltip: {
    edit: 'Edit in Studio',
    distribute: 'Distribute Track',
  },
  warningModal: {
    title: 'Confirm artist name',
    description:
      'By confirming your artist name, all releases will use this name. You will not be able to change this for future releases.',
    confirm: 'Confirm',
  },
  successModal: {
    title: 'Track submitted!',
    subTitle: 'Woohoo! Your track has been submitted for approval.',
    description:
      'This can take anywhere between 2-6 days. We’ll notify you via email once your track has been successfully approved.',
    submit: 'View my releases',
  },
  upgradeDistributionModal: {
    title: 'Upgrade to distribute music',
    subtitle:
      'To access this feature, simply add Distribution when you select a subscription tier on our pricing page.',
    upgrade: 'Upgrade',
    goBack: 'Maybe Later',
  },
  withdrawLimitUndercutDialog: {
    title: 'Cannot withdraw earnings',
    description:
      'In order to withdraw your earnings, you need to have at least generated a minimum of €10.00.',
    cta: 'OK',
  },
  payoutSuccessDialog: {
    title: 'Payout requested',
    description:
      "You'll receive an email from PayPal shortly with instructions to collect your earnings. Keep an eye on your inbox!",
    cta: 'OK, got it',
  },
  unknownPayoutError:
    'An unknown error happened during your payout. Please contact our support for help.',
  addedBlock: {
    title: 'Distribution added',
    description:
      'You will be able to release music and earn revenues from top streaming sites like <strong>Spotify</strong>, <strong>Apple Music</strong>, <strong>Amazon</strong> and more.',
    learnMore: 'Learn more',
  },
  licenseBlock: {
    title: 'Custom Licensing',
    description: 'Premium music solutions designed for you.',
    features: {
      apiIntegrations: 'API integrations',
      mediaStreaming: 'Film, TV, Video Streaming',
      musicLabels: 'Music Labels',
      physicalLocations: 'Physical locations',
      largeEnterprise: 'Large Enterprise',
      advertisingCampaigns: 'Large spend advertising campaigns',
      subLicensing: 'Sub-Licensing',
      partnerships: 'Partnerships',
    },
    getInTouch: 'Get in Touch',
  },
  noDistribution: 'No Distribution',
  description:
    'Get your music on Spotify, Apple Music, Amazon Music, YouTube Music and more...',
  monthly: 'Monthly',
  annually: 'Annually',
  infoSection: {
    comingSoon: ' — Coming soon',
    billedPeriod: '/per month billed annually',
    footerMessage:
      'The right to release & monetize your creations on music streaming sites is available exclusively via Loudly’s Distribution service',
  },
  from: 'From',
  releaseWarningModal: {
    title: 'Unable to release track',
    description: {
      songToShort:
        'Only tracks longer than 30 seconds can be released via Loudly.',
      notTheSongOwner:
        'This track is owned by another user, and cannot be released by you.',
      catalogSong:
        'You cannot release this track because it was generated from a song in our catalog.',
      songAlreadyReleased: 'You have already created a release for this track.',
    },
    submit: 'Generate New Track',
    cancel: 'Maybe later',
  },
  releaseLimitDialog: {
    title: 'Release limit reached.',
    description:
      'You have reached your 30-day limit. Come back in {{daysToReset}} days to release more tracks.',
    cta: 'Ok, got it',
  },
  upsellModal: {
    title: 'Get more, Go annual!',
    subtitle:
      'By switching your payment cycle to annual, you’ll get access to ',
    buttonText: 'Pay Annually & Save 20%',
    billedPeriod: '/ per month',
    amountBilledAnnually: 'Billed annually at ',
    includes: 'includes',
    labels: {
      distro: 'Distro',
      plus: 'Plus',
      basic: 'Basic',
    },
  },
  payout: {
    title: 'Revenue',
    subtitle: 'Amount inclusive of all released tracks.',
    description: {
      info: "Your earnings will be displayed here soon! We're awaiting revenue data from stores and streaming platforms, which typically takes three months. Thank you for your patience!",
      error: 'Earnings currently not available. Try again later.',
    },
    withdraw: 'Withdraw',
  },
  playbackStats: {
    title: 'Playback Stats',
    subtitle: 'Get insights into how your tracks are performing.',
    status: {
      up: 'Trending Upwards',
    },
    viewStats: 'View Stats',
    comingSoonDialog: {
      title: 'Coming Soon',
      description:
        "We're working hard on having playback stats for your released tracks. You'll be notified when this feature is ready!",
      action: 'Ok, got it',
    },
  },
  releasesStats: {
    title: 'Released tracks',
    subtitle: 'Total number of tracks you’ve released.',
    name: {
      released: 'Released',
      pending: 'Pending',
      draft: 'Draft',
    },
  },
  uploadAudioModal: {
    title: 'Upload Audio to release',
    instructions:
      'Must be original song between 30 seconds and 8 minutes in duration, and in WAV format.',
    agreement:
      'I certify that I own or control all rights to any content I upload using this Loudly feature. I understand I cannot upload content if I do not have these rights. If I do, I will breach a contract with Loudly and may face legal liability. By continuing, I acknowledge and agree to this.',
    hint: 'Drag and Drop or <0>Click to Upload</0>',
    uploadButton: {
      upload: 'Upload',
      uploading: 'Uploading...',
      continue: 'Continue',
    },
    cancel: 'Cancel',
    fileSizeUnits: {
      emptyFile: 'Empty file',
      bytes: 'Bytes',
      kb: 'KB',
      mb: 'MB',
    },
    validation: {
      message: {
        uploadingFile: 'Uploading file...',
        uploadComplete: 'Upload complete',
        invalidFileType:
          'Unable to upload this song, file format not supported. Please try another file.',
        audioFileTooLong:
          'The maximum song duration is 8 minutes. Please try another file.',
        audioFileToShort:
          'The minimum song duration is 30 seconds. Please try another file.',
        tooManyFiles: 'Only one file at a time is allowed.',
        generic: 'This song cannot be used, please try another',
      },
    },
  },
  cancelUploadAudioModal: {
    title: 'Cancel upload?',
    description: 'Your track will not be uploaded.',
    confirmCancelation: 'Yes, Cancel',
    continueUploading: 'Continue Uploading',
  },
  deleteDraftReleaseModal: {
    title: 'Delete draft release?',
    description:
      'Are you sure you want to delete this draft release? This action cannot be undone.',
    delete: 'Delete',
  },
  distributionInfoBlock: {
    title: 'Distribute music',
    subtitle:
      'Release music with Loudly Distro and earn 100% revenues from 50+ sites including Spotify, TikTok, Apple, YouTube Music, Amazon and more.',
    distroBasicSubtext: 'Included with all monthly payment plans',
    distroPlusSubtext: 'Included with all annual payment plans',
  },
  distributionInfoCta: 'Upgrade Now',
};
